import { audienceCategories } from './audienceCategories'
import { useCases } from './useCases'
import { PRESENTATIONS } from '../data/presentations'
import { TUTORIALS } from '../data/tutorials'
import {
  SCENARIOS,
  getScenarioByCategoryAndCase,
  getScenarioBySlug,
  getScenarioPath,
  getScenariosByCategorySlug,
  getAllCategoryHubPaths,
  resolveCategorySlug,
  scenarioCategorySlug,
} from '../data/scenarios'
import { isDisabledPractitionerPath } from './practitionerFeatures'

export const SITE_ORIGIN = 'https://livewello.com'
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/assets/partner/livewello-social-1200x630.png`

export interface PublicShareMeta {
  path: string
  /** Document `<title>` (browser tab). */
  title: string
  description: string
  /** Share-card headline; defaults to `title` when omitted. */
  ogTitle?: string
  ogImage?: string
  ogType?: 'website' | 'article'
}

export const DEFAULT_SHARE_META: PublicShareMeta = {
  path: '/',
  title: 'Livewello — Your Personal Health Intelligence Platform',
  description:
    'Livewello combines your genetics, health records, and personalized guidance to surface insights standard blood work misses. Upload your DNA, track your health, and understand what it all means.',
  ogImage: DEFAULT_OG_IMAGE,
  ogType: 'website',
}

/** Static public routes with fixed paths (English — used for link previews and SEO). */
export const STATIC_PUBLIC_PAGES: PublicShareMeta[] = [
  DEFAULT_SHARE_META,
  {
    path: '/plans-and-pricing',
    title: 'Plans & Pricing | Livewello',
    description:
      'Flexible plans for every wellness journey. Compare Free, Basic, and Premium — genetics, Wellness Coach, wellness goals, and more.',
  },
  {
    path: '/dna-kit',
    title: 'At-Home DNA Kit for Personalized Health Insights | Livewello',
    description:
      'Unlock your genetic insights with our FDA-compliant at-home DNA kit. Comprehensive coverage, secure processing, and personalized analysis.',
  },
  {
    path: '/terms',
    title: 'Terms of Service | Livewello',
    description: 'Livewello terms of service — subscription plans, AI services, genetic data rights, and health information disclaimers.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | Livewello',
    description:
      'How Livewello collects, uses, and protects your health data, genetic information, and account details.',
  },
  {
    path: '/how-to-use',
    title: 'How to Use Livewello',
    description:
      'Your health data, organized — with Coach that actually knows you. Guides for individuals, caregivers, and wellness practitioners.',
  },
  {
    path: '/claude',
    title: 'Connect Claude to Livewello',
    description:
      'Ask Claude about your genetics, health records, and wellness goals — and get personalized answers grounded in your own data.',
    ogImage: `${SITE_ORIGIN}/images/claude-setup/step-5-connected-conversation.png`,
  },
  {
    path: '/docs/ai',
    title: 'AI Integration Documentation | Livewello',
    description:
      'Technical documentation for connecting external AI tools to Livewello via MCP — genetics, health records, and wellness goals.',
  },
  {
    path: '/developers',
    title: 'Developer API | Livewello',
    description:
      'Integrate Livewello genetics and health records into your app with the read-only Developer REST API — SNP queries, health records, and profile access.',
  },
  {
    path: '/press',
    title: 'Press & Media | Livewello',
    description: 'Press resources, featured coverage, and media assets for Livewello — the personal health intelligence platform.',
  },
  {
    path: '/partners/phoenix',
    title: 'Livewello for APOE4 Carriers | Phoenix Partner',
    description:
      'Built for APOE4 carriers who want to go deeper. Upload your DNA, explore every APOE4-relevant variant, and research it all with Coach.',
    ogImage: `${SITE_ORIGIN}/images/partners/phoenix-logos/social graph 1200.630.png`,
  },
  {
    path: '/affiliates',
    title: 'Affiliate Program | Livewello',
    description:
      'Help people make sense of their health — share Livewello and earn 35% on referred subscriptions for 12 months, or 10% on DNA kits with 10% off for kit buyers.',
  },
  {
    path: '/practitioner-spotlight',
    title: 'Practitioner Spotlight | Livewello',
    description:
      'Get featured in our newsletter with a polished spotlight page — showcase your story, outcomes, and client feedback to attract new clients.',
  },
  {
    path: '/practitioner-spotlight/how-it-works',
    title: 'How Practitioner Spotlight Works | Livewello',
    description:
      'Interview, metrics, review, and publish — how Livewello helps wellness practitioners get featured and grow their practice.',
  },
  {
    path: '/directory',
    title: 'Find a Wellness Coach | Livewello',
    description:
      'Browse top-performing wellness coaches and practitioners in the Livewello directory.',
  },
  {
    path: '/login',
    title: 'Log In | Livewello',
    description: 'Sign in to your Livewello account — genetics, health records, Wellness Coach, and wellness goals.',
  },
  {
    path: '/register',
    title: 'Create Account | Livewello',
    description: 'Create your free Livewello account and start organizing your health data with personalized insights.',
  },
  {
    path: '/forgot-password',
    title: 'Forgot Password | Livewello',
    description: 'Reset your Livewello account password.',
  },
  {
    path: '/reset-password',
    title: 'Reset Password | Livewello',
    description: 'Choose a new password for your Livewello account.',
  },
  {
    path: '/coach/spotlight-demo',
    title: 'Practitioner Spotlight Demo | Livewello',
    description: 'Preview a sample practitioner spotlight page — the kind of feature page Livewello publishes for wellness coaches.',
  },
  {
    path: '/scenarios',
    title: 'Clinical Case Studies | Livewello',
    description:
      'Fictional-but-clinically-grounded case studies showing the insights that only emerge when genetics, health records, and personalized guidance work together.',
  },
]

function scenarioToShareMeta(scenario: (typeof SCENARIOS)[number]): PublicShareMeta {
  return {
    path: getScenarioPath(scenario),
    title: `${scenario.title} — Livewello Case Study`,
    ogTitle: scenario.title,
    description: scenario.socialHook,
    ogImage: scenario.meta.ogImage
      ? scenario.meta.ogImage.startsWith('http')
        ? scenario.meta.ogImage
        : `${SITE_ORIGIN}${scenario.meta.ogImage}`
      : DEFAULT_OG_IMAGE,
    ogType: 'article',
  }
}

function scenarioCategoryShareMeta(categorySlug: string, pathSlug: string): PublicShareMeta {
  const scenarios = getScenariosByCategorySlug(categorySlug)
  const label = scenarios[0]?.series ?? categorySlug
  return {
    path: `/scenarios/${pathSlug}`,
    title: `${label} Case Studies — Livewello`,
    description: `Fictional-but-clinically-grounded ${label.toLowerCase()} case studies showing what genetics and health records reveal together.`,
    ogType: 'website',
  }
}

function getScenarioShareMeta(path: string): PublicShareMeta | undefined {
  if (path === '/scenarios') {
    return STATIC_PUBLIC_PAGES.find((p) => p.path === '/scenarios')
  }

  const scenarioMatch = path.match(/^\/scenarios\/([^/]+)\/([^/]+)$/)
  if (scenarioMatch) {
    const [, category, slug] = scenarioMatch
    const resolvedCategory = resolveCategorySlug(category)
    const scenario = resolvedCategory ? getScenarioByCategoryAndCase(resolvedCategory, slug) : undefined
    if (scenario) {
      return scenarioToShareMeta(scenario)
    }
    return undefined
  }

  const categoryMatch = path.match(/^\/scenarios\/([^/]+)$/)
  if (!categoryMatch) return undefined

  const slug = categoryMatch[1]
  const scenario = getScenarioBySlug(slug)
  if (scenario) return scenarioToShareMeta(scenario)

  const categorySlug = resolveCategorySlug(slug)
  if (categorySlug) return scenarioCategoryShareMeta(categorySlug, slug)

  return undefined
}

function getHowToUseShareMeta(path: string): PublicShareMeta | undefined {
  if (path === '/how-to-use') {
    return STATIC_PUBLIC_PAGES.find((p) => p.path === '/how-to-use')
  }

  const match = path.match(/^\/how-to-use\/([^/]+)(?:\/([^/]+))?$/)
  if (!match) return undefined

  const [, categoryId, useCaseId] = match

  if (useCaseId) {
    const useCase = useCases.find((u) => u.id === useCaseId && u.category === categoryId)
    if (!useCase) return undefined
    return {
      path,
      title: `${useCase.title} | Livewello How-To`,
      description: useCase.description,
      ogType: 'article',
    }
  }

  const category = audienceCategories.find((c) => c.id === categoryId)
  if (!category) return undefined
  return {
    path,
    title: `${category.title} — How to Use Livewello`,
    description: category.description,
    ogType: 'website',
  }
}

function getTutorialShareMeta(path: string): PublicShareMeta | undefined {
  if (path === '/tutorials') {
    return {
      path: '/tutorials',
      title: 'Product Tutorials | Livewello',
      description:
        'Visual walkthroughs for Livewello on web and mobile — Wellness Coach, Goals, Health Records, Profile Sharing, and Goals Partners.',
      ogType: 'website',
    }
  }
  const match = path.match(/^\/tutorials\/(web|mobile)\/([^/]+)$/)
  if (!match) return undefined
  const tutorial = TUTORIALS.find((t) => t.slug === match[2] && t.platform === match[1])
  if (!tutorial) return undefined
  const ogImage = tutorial.meta.ogImage ?? tutorial.coverImage
  return {
    path,
    title: `${tutorial.title} (${tutorial.platform}) — Livewello Tutorials`,
    ogTitle: tutorial.title,
    description: tutorial.meta.description,
    ogImage: ogImage.startsWith('http') ? ogImage : `${SITE_ORIGIN}${ogImage}`,
    ogType: 'article',
  }
}

function getPresentationShareMeta(path: string): PublicShareMeta | undefined {
  if (path === '/presentations') {
    return {
      path: '/presentations',
      title: 'Presentations | Livewello',
      description: 'Recorded presentations and keynotes from the Livewello team on precision health, genomics, and personalized personal health records.',
      ogType: 'website',
    }
  }
  const match = path.match(/^\/presentations\/([^/]+)$/)
  if (!match) return undefined
  const presentation = PRESENTATIONS.find((p) => p.slug === match[1])
  if (!presentation) return undefined
  return {
    path,
    title: `${presentation.title} — Livewello Presentations`,
    ogTitle: presentation.title,
    description: presentation.meta.description,
    ogImage: presentation.coverImage.startsWith('http')
      ? presentation.coverImage
      : `${SITE_ORIGIN}${presentation.coverImage}`,
    ogType: 'article',
  }
}

export function normalizePathname(pathname: string): string {
  const path = pathname.split('?')[0].split('#')[0]
  if (path !== '/' && path.endsWith('/')) return path.slice(0, -1)
  return path || '/'
}

/** Resolve share metadata for a URL path (client-side routing and prerender). */
export function getShareMetaForPath(pathname: string): PublicShareMeta {
  const path = normalizePathname(pathname)

  if (isDisabledPractitionerPath(path)) {
    return DEFAULT_SHARE_META
  }

  const staticMeta = STATIC_PUBLIC_PAGES.find((p) => p.path === path)
  if (staticMeta) return staticMeta

  return (
    getScenarioShareMeta(path) ??
    getHowToUseShareMeta(path) ??
    getPresentationShareMeta(path) ??
    getTutorialShareMeta(path) ??
    DEFAULT_SHARE_META
  )
}

/** All routes that should receive prerendered HTML with baked-in meta tags. */
export function getAllPrerenderShareRoutes(): PublicShareMeta[] {
  const byPath = new Map<string, PublicShareMeta>()

  for (const page of STATIC_PUBLIC_PAGES) {
    if (!isDisabledPractitionerPath(page.path)) {
      byPath.set(page.path, page)
    }
  }

  for (const scenario of SCENARIOS) {
    byPath.set(getScenarioPath(scenario), scenarioToShareMeta(scenario))
  }

  for (const presentation of PRESENTATIONS) {
    const meta = getPresentationShareMeta(`/presentations/${presentation.slug}`)
    if (meta) byPath.set(meta.path, meta)
  }

  const tutorialsIndexMeta = getTutorialShareMeta('/tutorials')
  if (tutorialsIndexMeta) byPath.set(tutorialsIndexMeta.path, tutorialsIndexMeta)

  for (const tutorial of TUTORIALS) {
    const meta = getTutorialShareMeta(`/tutorials/${tutorial.platform}/${tutorial.slug}`)
    if (meta) byPath.set(meta.path, meta)
  }

  for (const hubPath of getAllCategoryHubPaths()) {
    const slug = hubPath.replace('/scenarios/', '')
    const categorySlug = resolveCategorySlug(slug)
    if (categorySlug) {
      byPath.set(hubPath, scenarioCategoryShareMeta(categorySlug, slug))
    }
  }

  for (const category of audienceCategories) {
    byPath.set(`/how-to-use/${category.id}`, {
      path: `/how-to-use/${category.id}`,
      title: `${category.title} — How to Use Livewello`,
      description: category.description,
      ogType: 'website',
    })
    for (const useCase of useCases.filter((u) => u.category === category.id)) {
      byPath.set(`/how-to-use/${category.id}/${useCase.id}`, {
        path: `/how-to-use/${category.id}/${useCase.id}`,
        title: `${useCase.title} | Livewello How-To`,
        description: useCase.description,
        ogType: 'article',
      })
    }
  }

  return Array.from(byPath.values())
}

export function getShareMetaUrl(meta: PublicShareMeta): string {
  return `${SITE_ORIGIN}${meta.path}`
}

const SITEMAP_EXCLUDED_PATHS = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/coach/spotlight-demo',
])

/** Indexable public routes for sitemap.xml (deduped, no auth pages). */
export function getSitemapRoutes(): PublicShareMeta[] {
  return getAllPrerenderShareRoutes().filter((meta) => !SITEMAP_EXCLUDED_PATHS.has(meta.path))
}

export { scenarioCategorySlug, getScenarioPath }
